import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import GradientBackground from '../home/GradientBackground';
import { getAppBySlug } from '../../data/appsData';
import s from '../../styles/AppDetail.module.css';

function ScreenshotsSlider({ screenshots, appName }) {
  const rowRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const updateArrows = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollBy({ left: e.deltaY * 1.5, behavior: 'smooth' });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onMouseDown = (e) => {
    const el = rowRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  };

  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    const el = rowRef.current;
    if (!el) return;
    const dx = e.pageX - el.offsetLeft - drag.current.startX;
    el.scrollLeft = drag.current.scrollLeft - dx;
  };

  const onMouseUp = () => {
    drag.current.active = false;
    const el = rowRef.current;
    if (el) { el.style.cursor = 'grab'; el.style.userSelect = ''; }
  };

  const scroll = (dir) => {
    rowRef.current?.scrollBy({ left: dir * 428, behavior: 'smooth' });
  };

  return (
    <div className={s.screenshotsSlider}>
      <div
        className={s.screenshotsRow}
        ref={rowRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{ cursor: 'grab' }}
      >
        {screenshots.map((src, i) => (
          <div key={i} className={s.screenshotFrame}>
            <img
              src={src}
              alt={`${appName} screenshot ${i + 1}`}
              className={s.screenshotImg}
              draggable={false}
            />
          </div>
        ))}
      </div>
      {canLeft && (
        <button className={`${s.sliderArrow} ${s.sliderArrowLeft}`} onClick={() => scroll(-1)} aria-label="Previous">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="20" height="20">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {canRight && (
        <button className={`${s.sliderArrow} ${s.sliderArrowRight}`} onClick={() => scroll(1)} aria-label="Next">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="20" height="20">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function StoreBadge({ type, url }) {
  const isApple = type === 'apple';
  const label = isApple ? 'App Store' : 'Google Play';
  const sublabel = isApple ? 'Download on the' : 'Get it on';

  const icon = isApple
    ? <img src="/designs/icons/app-store-icon.png" alt="App Store" width="24" height="24" style={{ objectFit: 'contain' }} />
    : <img src="/designs/icons/google-play-icon.png" alt="Google Play" width="24" height="24" style={{ objectFit: 'contain' }} />;

  if (!url || url === '#') {
    return (
      <div className={`${s.badge} ${s.badgeDisabled}`}>
        {icon}
        <div>
          <div className={s.badgeSub}>{sublabel}</div>
          <div className={s.badgeMain}>{label}</div>
        </div>
        <svg className={s.badgeLock} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className={s.badge}>
      {icon}
      <div>
        <div className={s.badgeSub}>{sublabel}</div>
        <div className={s.badgeMain}>{label}</div>
      </div>
    </a>
  );
}

export default function AppDetail() {
  const { slug } = useParams();
  const app = getAppBySlug(slug);

  useEffect(() => {
    if (app) document.title = `${app.name} — Goodwavelab`;
  }, [app]);

  if (!app) return <Navigate to="/apps" replace />;

  return (
    <>
      <GradientBackground />
      <div className={s.page}>

        <Link to="/apps" className={s.backLink}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          All Apps
        </Link>

        {/* Hero */}
        <section className={s.hero}>
          <div className={s.heroIcon} style={{ background: app.icon ? 'transparent' : app.accentColor }}>
            {app.icon
              ? <img src={app.icon} alt={app.name} className={s.heroIconImg} />
              : app.name.charAt(0)
            }
          </div>
          <div className={s.heroText}>
            <div className={s.heroPlatforms}>
              {app.platforms.includes('ios') && <span className={s.platform}>iOS</span>}
              {app.platforms.includes('android') && <span className={s.platform}>Android</span>}
            </div>
            <h1 className={s.heroTitle}>{app.name}</h1>
            {app.nameIos !== app.nameAndroid && (
              <div className={s.platformNames}>
                {app.platforms.includes('ios') && (
                  <span className={s.platformName}>
                    <img src="/designs/icons/app-store-icon.png" alt="App Store" width="13" height="13" style={{ objectFit: 'contain', flexShrink: 0 }} />
                    {app.nameIos}
                  </span>
                )}
                {app.platforms.includes('android') && (
                  <span className={s.platformName}>
                    <img src="/designs/icons/google-play-icon.png" alt="Google Play" width="13" height="13" style={{ objectFit: 'contain', flexShrink: 0 }} />
                    {app.nameAndroid}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={s.heroBadges}>
            {app.platforms.includes('ios') && (
              <StoreBadge type="apple" url={app.appStoreUrl} />
            )}
            {app.platforms.includes('android') && (
              <StoreBadge type="google" url={app.googlePlayUrl} />
            )}
          </div>
        </section>

        {/* Screenshots — above Key Features */}
        {app.screenshots && app.screenshots.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Screenshots</h2>
            <ScreenshotsSlider screenshots={app.screenshots} appName={app.name} />
          </section>
        )}

        {/* Description — below Screenshots */}
        <p className={s.heroDesc}>{app.description}</p>

        {/* Key Features */}
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Key Features</h2>
          <div className={s.featuresGrid}>
            {app.features.map((f, i) => (
              <div key={i} className={s.featureCard}>
                <div className={s.featureDot} style={{ background: app.accentColor }} />
                <span className={s.featureText}>{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Legal */}
        {(app.privacyPath || app.termsPath || app.accountRemovePath) && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Legal</h2>
            <div className={s.legalLinks}>
              {app.privacyPath && (
                <Link to={app.privacyPath} className={s.legalLink}>Privacy Policy</Link>
              )}
              {app.termsPath && (
                <Link to={app.termsPath} className={s.legalLink}>Terms of Use</Link>
              )}
              {app.accountRemovePath && (
                <Link to={app.accountRemovePath} className={s.legalLink}>Account Removal</Link>
              )}
            </div>
          </section>
        )}

      </div>
    </>
  );
}
